/**
 * Name search on the dashboard detail lists.
 *
 * Every word typed has to turn up somewhere in the member's name, in any
 * order — "doe john" finds "John M Doe", "jr" finds "Frank, Jr." — and case,
 * accents and stray spaces are ignored. The rows print first names only, so the
 * match runs over the record's own name parts as well as the printed name: a
 * reader who knows the family by surname can still type it.
 */

/** Lower-case, accent-stripped, single-spaced. */
export function normalizeDashboardSearchText(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** True when the query is blank or every word of it appears in the text. */
export function dashboardSearchMatches(text, query) {
  const terms = normalizeDashboardSearchText(query).split(" ").filter(Boolean);
  if (!terms.length) return true;

  const haystack = normalizeDashboardSearchText(text);
  return terms.every((term) => haystack.includes(term));
}

const readNamePart = (member, ...keys) => {
  for (const key of keys) {
    const value = member?.[key];
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return "";
};

/**
 * Everything a member can be found by: the name as printed on the row, then
 * the first, middle, last, maiden and special names off the record itself.
 */
export function getDashboardMemberSearchText(member, printedName = "") {
  return [
    printedName,
    readNamePart(member, "first_name", "firstName"),
    readNamePart(member, "middle_name", "middleName"),
    readNamePart(member, "last_name", "lastName"),
    readNamePart(member, "maiden_name", "maidenName"),
    readNamePart(member, "special_name", "specialName"),
    readNamePart(member, "nickname", "nick_name"),
    readNamePart(member, "suffix"),
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * The entries that match the query, in their original order. `getPrintedName`
 * is the page's own name formatter, so the search sees exactly what the row
 * prints. Pages whose rows wrap the member record pass `getMember` to reach
 * it; by default the entry is the record. A blank query hands the list back
 * untouched.
 */
export function filterDashboardMembersBySearch(
  entries,
  query,
  getPrintedName = () => "",
  getMember = (entry) => entry
) {
  if (!normalizeDashboardSearchText(query)) return entries;

  return entries.filter((entry) =>
    dashboardSearchMatches(
      getDashboardMemberSearchText(getMember(entry), getPrintedName(entry)),
      query
    )
  );
}
